import React, { FC } from 'react'
import { useHistory } from 'react-router-dom'
import { CheckIcon, XIcon } from '@heroicons/react/outline'
import usePricing from '../../../redux/pricing/usePricing'
import useCart from '../../../redux/shopping-cart/useCart'
import { Heading, VideoPlayer, Button, Dialog } from '../../System'
import { CourseType } from '../../../types'
import { getIconFromString } from '../../System/IconPicker/getIconFromString'
import { StyledCourseTrailerModal } from '.'
import { CartPrice } from '../../Cart'
import useSegment from '../../utils/useSegment'

type Props = {
  isOpen: boolean;
  course: CourseType & { isOwned?: boolean };
  onClose: () => void;
}

const CourseTrailerModal: FC<Props> = ({ isOpen, course, onClose }) => {
  const { track } = useSegment()
  const { actions: { getPriceData } } = usePricing()
  const { actions: { idInCart, addToCart } } = useCart()

  const history = useHistory()
  const coursePrice = getPriceData('course')
  const inCart = idInCart(course._id)

  const handleAddToCart = () => {
    if (!idInCart(course._id)) {
      return addToCart({ itemData: course, type: 'course' }, 'course modal')
    }

    history.push('/checkout')
  }

  // Track when the user starts the course's trailer video
  const trackVideoStart = () => {
    track('Viewed Trailer', {
      id: course.teaser?.svid,
      course_title: course.title,
      course_id: course._id
    })
  }

  return (
    <Dialog isOpen={isOpen} onClose={() => onClose()}>
      <StyledCourseTrailerModal>
        {course?.teaser?.svid && (
          <VideoPlayer videoId={course.teaser.svid} onStart={() => trackVideoStart()} />
        )}
        {(course.offeringsDescription.length > 0 || course.includesDescription.length > 0) && (
          <div className="bullet-section">
            {course.offeringsDescription.length > 0 && (
              <div className="course-bullets-container">
                <Heading type="h2">WHAT YOU'LL LEARN</Heading>
                <ul className="course-bullets">
                  {course.offeringsDescription.map((bullet, index) => {
                    return (
                      <li key={index}>
                        <CheckIcon />
                        {bullet}
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
            {course.includesDescription.length > 0 && (
              <div className="course-bullets-container">
                <Heading type="h2">THIS COURSE INCLUDES</Heading>
                <ul className="course-bullets">
                  {course.includesDescription.map((bullet, index) => {
                    return (
                      <li key={index}>
                        {getIconFromString(bullet.icon) || <XIcon />}
                        {bullet.label}
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
        {!course.isOwned && (
          <div className="course-actions">
            {coursePrice && <CartPrice item={coursePrice} />}
            <Button
              onClick={() => handleAddToCart()}
              isActive={inCart}
              iconPos="left"
              theme={inCart ? 'outline' : 'solid'}
            >
              {inCart ? (
                <>
                  <CheckIcon /> In Cart
                </>
              ) : (
                'Add to Cart'
              )}
            </Button>
          </div>
        )}
      </StyledCourseTrailerModal>
    </Dialog>
  )
}

export default CourseTrailerModal
